import { proxy } from 'valtio'
import { derive, devtools } from 'valtio/utils'
import { type ApiCallType } from '../types/ApiCallType'
import { type RecurringPaymentStatus } from '../types/GlobalRecurringPaymentsStatus'
import { type GlobalCustomerCardType } from '../types/GlobalCustomerCardType'
import { type GlobalCustomerAchType } from '../types/GlobalCustomerAchType'
import { type Pagination } from '../../routes/app/data-exports/index/dataExportsProxy'
import { DEFAULT_LIMIT } from '../utils/apiUtils'
import { type GlobalCustomerType } from '../types/GlobalCustomerType'
import { type RpOccurrence } from '../../routes/app/recurring-charges/[id]/rp-occurrence/rpOccurrenceProxy'
import {
  PaymentResolveRadioValues
} from '../../routes/app/recurring-charges/[id]/rp-occurrence/[id]/utils/PaymentResolveRadioValues'

export interface RecurringPayment {
  id: string
  addressOne: string
  addressTwo: string
  city: string
  state: string
  postalCode: string
  countryCode: string
  email: string
  userId: number
  customerId: string
  customer?: GlobalCustomerType
  customerCardId: string
  achAccountId: string
  ['customerCard']?: GlobalCustomerCardType
  ['customerAch']?: GlobalCustomerAchType
  sslTxnId: string
  endDate: string
  startPaymentDate: string
  lastPaymentDate: string
  nextPaymentDate: string
  billingCycle: string
  billOnHalf: number
  endOfMonth: string
  skipNextPayment: boolean
  stopOnError: boolean
  invoiceNumber: string
  failedCount: number
  notes: string
  dynamicDba: string
  sslRecurringId: string
  amountCents: number
  amountCurrencyType: string
  totalAmountChargedCents: number
  totalAmountChargedCurrencyType: string
  salesTax: number
  createdDate: string
  updatedAt: null | string
  status: RecurringPaymentStatus
}

export interface LinkedPayment {
  cardType: string | null
  cardNumber: string | null
  expDate: string | null
  numberOfPayments: string | null
  firstName: string | null
  lastName: string | null
  address: string | null
  zip: string | null
  startPaymentDate: string | null
  nextPaymentDate: string | null
  billingCycle: string | null
  invoiceNumber: string | null
  sslRecurringId: string | null
  amount: number
  lastPaymentDate: string | null
}

export interface GlobalRecurringPaymentType {
  apiCall: ApiCallType
  currentPage: number
  linkedApiCall: ApiCallType
  relatedApiCall: ApiCallType
  suspendApiCall: ApiCallType
  skipApiCall: ApiCallType
  retryApiCall: ApiCallType
  recurringPayment: RecurringPayment | null
  futureOccurrences: string[]
  linkedRecurringPayments: LinkedPayment | null
  rpOccurrences: RpOccurrence[]
  pagination: Pagination
  nextPaymentDate: string | null
  displayPauseModal: boolean
  displayEndModal: boolean
  displayResumeModal: boolean
  resolveRadioValue: string
}

const baseProxy = proxy({
  apiCall: {
    error: null,
    loading: false
  },
  currentPage: 1,
  displayEndModal: false,
  displayPauseModal: false,
  displayResumeModal: false,
  futureOccurrences: [],
  linkedApiCall: {
    error: null,
    loading: false
  },
  linkedRecurringPayments: null,
  nextPaymentDate: null,
  pagination: {
    limit: DEFAULT_LIMIT,
    offset: 0,
    totalCount: 0
  },
  recurringPayment: null,
  relatedApiCall: {
    error: null,
    loading: false
  },
  resolveRadioValue: PaymentResolveRadioValues.DONT_RESUME_PAYMENT,
  retryApiCall: {
    error: null,
    loading: false
  },
  rpOccurrences: [],
  skipApiCall: {
    error: null,
    loading: false
  },
  suspendApiCall: {
    error: null,
    loading: false
  }
})

export const recurringPaymentProxy = derive({}, { proxy: baseProxy }) as GlobalRecurringPaymentType

devtools(recurringPaymentProxy, { name: 'recurringPaymentProxy' })
